import { cva } from "class-variance-authority";

/**
 * Tag Variants
 *
 * V&A Waterfront Design System - Tag Component
 * Figma: https://www.figma.com/design/CV2ePEn9S0SSZ7T3AAPqF4/V-A-Waterfront---Website---Foundation-Design-System?node-id=10817-103331
 *
 * Styles:
 * - light: Grey background (bg-deep-sea-blue-50), blue text - for light backgrounds
 * - dark: Blue background (bg-open-water-500), white text - for dark backgrounds
 * - transparent-dark: Semi-transparent dark overlay with blur
 * - transparent-light-white-text: Semi-transparent with white text (for image overlays)
 * - transparent-light-dark-text: Semi-transparent with dark text (for image overlays)
 * - deal-status: Status indicators (redeemed/expired)
 * - app-section: Colored backgrounds for app sections
 *
 * Sizes:
 * - md: 24px height, 14px text (default)
 * - sm: 20px height, 12px text
 */

export const tagVariants = cva(
  // Base styles
  [
    "inline-flex items-center justify-center gap-2",
    "whitespace-nowrap overflow-hidden text-ellipsis",
    "pl-2 pr-2 py-1",
    "rounded-sm",
  ],
  {
    variants: {
      variant: {
        // Light - Default for light backgrounds
        light: ["bg-deep-sea-blue-50", "text-open-water-500"],

        // Dark - Filled blue background
        dark: ["bg-open-water-500", "text-white"],

        // Transparent Dark - Semi-transparent overlay
        "transparent-dark": [
          "bg-grey-600/75",
          "backdrop-blur-sm",
          "text-white",
        ],

        // Transparent Light with White Text - For dark image overlays
        "transparent-light-white-text": [
          "bg-white/20",
          "backdrop-blur-sm",
          "text-white",
        ],

        // Transparent Light with Dark Text - For light image overlays
        "transparent-light-dark-text": [
          "bg-white/20",
          "backdrop-blur-sm",
          "text-deep-sea-blue-500",
        ],

        // Deal Status - Base style, colors set by status
        "deal-status": ["bg-deep-sea-blue-50", "text-open-water-500"],

        // App Section - Base style, colors set by status
        "app-section": ["bg-deep-sea-blue-50", "text-open-water-500"],
      },
      size: {
        // Medium - 24px height
        md: ["h-6", "typography-component-xs-medium"],

        // Small - 20px height, 12px text (Components/Medium(500)/XXS)
        sm: ["h-5", "typography-component-xxs-medium"],
      },
      status: {
        default: [],
        redeemed: [],
        expired: [],
        "my-va": [],
        neighbourhood: [],
        deals: [],
        "whats-on": [],
        account: [],
      },
    },
    compoundVariants: [
      // Deal Status - Redeemed (green tint)
      {
        variant: "deal-status",
        status: "redeemed",
        className: "bg-avocado-300 text-open-water-500",
      },
      // Deal Status - Expired (grey tint)
      {
        variant: "deal-status",
        status: "expired",
        className: "bg-deep-sea-blue-50 text-grey-500",
      },

      // App Section Colors - My V&A (acid green)
      {
        variant: "app-section",
        status: "my-va",
        className: "bg-acid-green text-open-water-500",
      },
      // App Section Colors - Neighbourhood (peach/orange)
      {
        variant: "app-section",
        status: "neighbourhood",
        className: "bg-peach text-open-water-500",
      },
      // App Section Colors - Deals (afternoon blue)
      {
        variant: "app-section",
        status: "deals",
        className: "bg-afternoon-200 text-open-water-500",
      },
      // App Section Colors - What's On (avocado green)
      {
        variant: "app-section",
        status: "whats-on",
        className: "bg-avocado-300 text-open-water-500",
      },
      // App Section Colors - Account (princess pink)
      {
        variant: "app-section",
        status: "account",
        className: "bg-princess-200 text-open-water-500",
      },
    ],
    defaultVariants: {
      variant: "light",
      size: "md",
      status: "default",
    },
  },
);
